/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { keysUpdatePolicy } from "../funcs/keysUpdatePolicy.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Key } from "../models/components/key.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateAPIKeyPolicyRequest,
  UpdateAPIKeyPolicySecurity,
} from "../models/operations/updateapikeypolicy.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateAPIKeyPolicyMutationVariables = {
  request: UpdateAPIKeyPolicyRequest;
  security?: UpdateAPIKeyPolicySecurity | undefined;
  options?: RequestOptions;
};

export type UpdateAPIKeyPolicyMutationData = Key;

export type UpdateAPIKeyPolicyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateKeyPolicy keys
 *
 * @remarks
 * Replace an ordinary key's own policy: the mcp:connect and mcp:auth grants, and their blocks, its server and auth client access is evaluated against. Agent keys keep their delegated grants. Requires org admin.
 */
export function useUpdateAPIKeyPolicyMutation(
  options?: MutationHookOptions<
    UpdateAPIKeyPolicyMutationData,
    UpdateAPIKeyPolicyMutationError,
    UpdateAPIKeyPolicyMutationVariables
  >,
): UseMutationResult<
  UpdateAPIKeyPolicyMutationData,
  UpdateAPIKeyPolicyMutationError,
  UpdateAPIKeyPolicyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateAPIKeyPolicyMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateAPIKeyPolicy(): MutationKey {
  return ["@gram/client", "keys", "updatePolicy"];
}

export function buildUpdateAPIKeyPolicyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateAPIKeyPolicyMutationVariables,
  ) => Promise<UpdateAPIKeyPolicyMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateAPIKeyPolicy(),
    mutationFn: function updateAPIKeyPolicyMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateAPIKeyPolicyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(keysUpdatePolicy(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
